import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import {
  Search,
  TrendingUp,
  MousePointerClick,
  Eye,
  RefreshCw,
  Globe,
  AlertCircle
} from 'lucide-react'

export function SEO() {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['seo', 'overview'],
    queryFn: () => api.getSEOOverview(),
  })

  const syncMutation = useMutation({
    mutationFn: () => api.syncSEOData(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo'] })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card h-24 animate-pulse bg-slate-100" />
          ))}
        </div>
        <div className="card h-64 animate-pulse bg-slate-100" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">SEO Intelligence</h2>
          <p className="text-sm text-slate-500">Search performance across all properties</p>
        </div>
        <button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          className="btn-primary flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          Sync Data
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Clicks"
          value={data?.totalClicks?.toLocaleString() || '0'}
          icon={MousePointerClick}
          color="blue"
        />
        <StatCard
          title="Impressions"
          value={data?.totalImpressions?.toLocaleString() || '0'}
          icon={Eye}
          color="green"
        />
        <StatCard
          title="Average CTR"
          value={`${((data?.averageCTR || 0) * 100).toFixed(2)}%`}
          icon={TrendingUp}
          color="purple"
        />
        <StatCard
          title="Avg Position"
          value={data?.averagePosition?.toFixed(1) || '-'}
          icon={Search}
          color="orange"
        />
      </div>

      {/* Properties */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Connected Properties</h3>

        {data?.properties && data.properties.length > 0 ? (
          <div className="space-y-3">
            {data.properties.map((property) => (
              <div
                key={property.id}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    property.type === 'google_search_console' ? 'bg-blue-100' :
                    property.type === 'google_analytics' ? 'bg-orange-100' :
                    'bg-green-100'
                  }`}>
                    <Globe className={`w-5 h-5 ${
                      property.type === 'google_search_console' ? 'text-blue-600' :
                      property.type === 'google_analytics' ? 'text-orange-600' :
                      'text-green-600'
                    }`} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{property.domain}</p>
                    <p className="text-sm text-slate-500 capitalize">
                      {property.type.replace(/_/g, ' ')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {property.lastSync && (
                    <span className="text-sm text-slate-500">
                      Last sync: {new Date(property.lastSync).toLocaleDateString()}
                    </span>
                  )}
                  <span className={`badge ${
                    property.status === 'active' ? 'badge-success' : 'badge-error'
                  }`}>
                    {property.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No properties connected</h3>
            <p className="text-slate-500 mb-4">
              Connect Google Search Console, Analytics, or Bing Webmaster to see SEO data.
            </p>
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="card bg-primary-50 border-primary-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-primary-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-primary-900">SEO Intelligence Tips</h4>
            <ul className="mt-2 text-sm text-primary-700 space-y-1">
              <li>• Monitor your average position to track ranking improvements</li>
              <li>• A CTR below 2% may indicate your titles need optimization</li>
              <li>• Use the AI content generator to create SEO-optimized articles</li>
              <li>• Sync regularly to keep your data fresh</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string
  value: string
  icon: typeof Search
  color: 'blue' | 'green' | 'purple' | 'orange'
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  }

  return (
    <div className="card">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  )
}
